// Centralized route-aware SEO head management.
// This module only mutates document.title and the meta/link tags responsible for
// title, description, keywords, canonical, Open Graph, and Twitter Card metadata.
// Schema.org JSON-LD is handled separately in src/lib/schema.ts.

export const SITE_NAME = 'CSSBuy Spreadsheet';

export const FAVICON_URL =
  'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/d367cf6745ee47f3fc82c76d3a54c50c.png';

export const SITE_URL =
  (import.meta.env.VITE_SITE_URL as string | undefined) ||
  (typeof window !== 'undefined' ? window.location.origin : '');

export interface SeoData {
  title: string;
  description: string;
  keywords: string;
  /** Route path used to build the canonical URL, e.g. "/" or "/article/slug". */
  path: string;
  ogType?: 'website' | 'article';
  image?: string;
}

function ensureMeta(attr: 'name' | 'property', key: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function removeMeta(attr: 'name' | 'property', key: string): void {
  const el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (el) el.remove();
}

function ensureLink(rel: string, href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function buildCanonical(path: string): string {
  return `${SITE_URL}${path}`;
}

export function applySeo(seo: SeoData): void {
  document.title = seo.title;

  ensureMeta('name', 'description', seo.description);
  ensureMeta('name', 'keywords', seo.keywords);
  ensureMeta('name', 'robots', 'index, follow');

  const canonical = buildCanonical(seo.path);
  ensureLink('canonical', canonical);

  ensureMeta('property', 'og:title', seo.title);
  ensureMeta('property', 'og:description', seo.description);
  ensureMeta('property', 'og:type', seo.ogType || 'website');
  ensureMeta('property', 'og:url', canonical);
  ensureMeta('property', 'og:site_name', SITE_NAME);

  ensureMeta('name', 'twitter:card', 'summary_large_image');
  ensureMeta('name', 'twitter:title', seo.title);
  ensureMeta('name', 'twitter:description', seo.description);

  if (seo.image) {
    ensureMeta('property', 'og:image', seo.image);
    ensureMeta('name', 'twitter:image', seo.image);
  } else {
    removeMeta('property', 'og:image');
    removeMeta('name', 'twitter:image');
  }
}

export function applyNoIndex(): void {
  ensureMeta('name', 'robots', 'noindex, nofollow');
}